/**
 * RBAC Service (Frontend)
 * API calls för Role-Based Access Control
 */

import api from '../api/client';
import type {
  Feature,
  Action,
  Role,
  UserPermissions,
  CreateRoleRequest,
  UpdateRoleRequest,
  AssignRoleRequest,
} from '@/types/rbac';

export type RolesScope = 'system' | 'organization';

export const rbacService = {
  getFeatures: async (): Promise<Feature[]> => {
    const response = await api.get('/api/rbac/features');
    return response.data.data;
  },

  getAvailableFeatures: async (orgScoped = false): Promise<Feature[]> => {
    const response = await api.get('/api/rbac/features/available', {
      params: orgScoped ? { orgScoped: true } : undefined,
    });
    return response.data.data;
  },

  getActionsByFeature: async (featureId: string): Promise<Action[]> => {
    const response = await api.get(`/api/rbac/features/${featureId}/actions`);
    return response.data.data;
  },

  getAllActions: async (): Promise<Record<string, Action[]>> => {
    const response = await api.get('/api/rbac/features/actions/all');
    return response.data.data;
  },

  getAvailableActions: async (orgScoped = false): Promise<Record<string, Action[]>> => {
    const response = await api.get('/api/rbac/features/actions/available', {
      params: orgScoped ? { orgScoped: true } : undefined,
    });
    return response.data.data;
  },

  getRoles: async (includeInactive = false, scope?: RolesScope): Promise<Role[]> => {
    const response = await api.get('/api/rbac/roles', {
      params: { includeInactive, scope },
    });
    return response.data.data;
  },

  getRoleTemplates: async (): Promise<Role[]> => {
    const response = await api.get('/api/rbac/role-templates');
    return response.data.data;
  },

  getRoleTemplatePermissions: async (
    templateId: string
  ): Promise<Array<{ feature_id: string; action_id?: string }>> => {
    const response = await api.get(`/api/rbac/role-templates/${templateId}/permissions`);
    return response.data.data;
  },

  getActiveRolesForAssignment: async (organizationId?: string): Promise<Role[]> => {
    const response = await api.get('/api/rbac/roles/active', {
      params: { organizationId },
    });
    return response.data.data;
  },

  toggleRoleActive: async (roleId: string, isActive: boolean): Promise<Role> => {
    const response = await api.patch(`/api/rbac/roles/${roleId}/toggle`, {
      is_active: isActive,
    });
    return response.data.data;
  },

  getRoleById: async (roleId: string): Promise<Role> => {
    const response = await api.get(`/api/rbac/roles/${roleId}`);
    return response.data.data;
  },

  createRole: async (data: CreateRoleRequest, scope: RolesScope = 'organization'): Promise<Role> => {
    const response = await api.post('/api/rbac/roles', data, {
      params: { scope },
    });
    return response.data.data;
  },

  updateRole: async (roleId: string, data: UpdateRoleRequest): Promise<Role> => {
    const response = await api.put(`/api/rbac/roles/${roleId}`, data);
    return response.data.data;
  },

  assignRoleToUser: async (data: AssignRoleRequest): Promise<void> => {
    await api.post(`/api/rbac/users/${data.user_id}/roles`, data);
  },

  removeRoleFromUser: async (userId: string, roleId: string): Promise<void> => {
    await api.delete(`/api/rbac/users/${userId}/roles/${roleId}`);
  },

  getUserPermissions: async (
    userId?: string,
    organizationId?: string
  ): Promise<UserPermissions> => {
    const endpoint =
      userId && organizationId
        ? `/api/rbac/permissions/${userId}/${organizationId}`
        : '/api/rbac/permissions/me';
    const response = await api.get(endpoint);
    return response.data.data;
  },
};
